import axios from 'axios'
import {
    fallbackPermissionGroups,
    normalizePermissionGroups
} from '@/config/permissions'
axios.defaults.headers.common['Accept'] = 'application/json'

/**
 * Rôles et catalogue de permissions d'une commune. `rootState.baseURL` vaut
 * déjà `<backend>/api/<companyId>`, donc tout est nativement scopé.
 *
 * Les écritures renvoient `{ roles: [...] }` (plus `role` quand elles en
 * produisent un) : la liste est réécrite depuis la réponse, sans re-GET.
 */
function withoutUndefined(payload) {
    return Object.entries(payload).reduce((acc, [key, value]) => {
        if (value !== undefined) acc[key] = value
        return acc
    }, {})
}

export default {
    getRoles({ rootState, commit }) {
        return new Promise((resolve, reject) => {
            commit('setLoading', true)
            axios
                .get(`${rootState.baseURL}/roles`)
                .then(({ data }) => {
                    commit('setRoles', data.roles)
                    commit('setLoading', false)
                    resolve(data.roles)
                })
                .catch((error) => {
                    commit('setLoading', false)
                    reject(error)
                })
        })
    },

    /**
     * Catalogue traduit côté serveur. En cas d'échec on retombe sur le
     * catalogue local, pour que l'écran reste utilisable.
     */
    getPermissionCatalogue({ rootState, commit }) {
        return new Promise((resolve, reject) => {
            axios
                .get(`${rootState.baseURL}/permissions`)
                .then(({ data }) => {
                    const groups = normalizePermissionGroups(data.groups)
                    commit('setPermissionGroups', groups.length ? groups : fallbackPermissionGroups())
                    resolve(groups)
                })
                .catch((error) => {
                    commit('setPermissionGroups', fallbackPermissionGroups())
                    reject(error)
                })
        })
    },

    createRole({ rootState, commit }, { label, description, permissions }) {
        return new Promise((resolve, reject) => {
            axios
                .post(
                    `${rootState.baseURL}/roles`,
                    withoutUndefined({ label, description, permissions })
                )
                .then(({ data }) => {
                    commit('setRoles', data.roles)
                    resolve(data.role)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    /**
     * `label`, `description` et `permissions` sont facultatifs côté API.
     * Omettre `permissions` laisse les droits du rôle intacts ; un tableau vide
     * les retire tous.
     */
    updateRole({ rootState, commit }, { id, label, description, permissions }) {
        return new Promise((resolve, reject) => {
            axios
                .put(
                    `${rootState.baseURL}/roles/${id}`,
                    withoutUndefined({ label, description, permissions })
                )
                .then(({ data }) => {
                    commit('setRoles', data.roles)
                    resolve(data.role)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    duplicateRole({ rootState, commit }, { id, label }) {
        return new Promise((resolve, reject) => {
            axios
                .post(`${rootState.baseURL}/roles/${id}/duplicate`, { label })
                .then(({ data }) => {
                    commit('setRoles', data.roles)
                    resolve(data.role)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    /**
     * `reassignTo` bascule les utilisateurs du rôle supprimé vers un autre rôle.
     */
    deleteRole({ rootState, commit }, { id, reassignTo }) {
        const params = reassignTo ? { reassign_to: reassignTo } : {}
        return new Promise((resolve, reject) => {
            axios
                .delete(`${rootState.baseURL}/roles/${id}`, { params })
                .then(({ data }) => {
                    commit('setRoles', data.roles)
                    resolve(data.roles)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    }
}
